import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ConfirmProvider } from "./components/Confirm";
import { DevBanner, isDev } from "./components/DevBanner";
import { installErrorReporting, reportError } from "./lib/errorReporting";

const queryClient = new QueryClient({
    defaultOptions: {
        // Realtime events drive freshness, so cached data can be trusted until told otherwise.
        queries: { staleTime: 60_000, refetchOnWindowFocus: false, retry: 1 },
    },
});

const container = document.getElementById("root") as HTMLElement;
const makeRoot = () => {
    installErrorReporting();
    // Errors that crash the React tree also go to the server log.
    return createRoot(container, { onUncaughtError: (err) => reportError("react", err) });
};
// Reuse the root across hot reloads so state survives edits. Bun only allows `import.meta.hot.data`
// written out in full (not through a variable).
function reusedRoot(): ReturnType<typeof makeRoot> {
    if (!import.meta.hot) return makeRoot();
    import.meta.hot.data.root ??= makeRoot();
    return import.meta.hot.data.root;
}
const root = reusedRoot();
root.render(
    <StrictMode>
        <QueryClientProvider client={queryClient}>
            <ConfirmProvider>
                {isDev ? (
                    <div className="flex h-full flex-col">
                        <DevBanner />
                        <div className="min-h-0 flex-1">
                            <App />
                        </div>
                    </div>
                ) : (
                    <App />
                )}
            </ConfirmProvider>
        </QueryClientProvider>
    </StrictMode>,
);
